// The Share button in a page's top bar, and the dialog behind it: who the page
// is shared with, a field for sharing it with someone else, and a way to stop
// sharing with each of them.
//
// The button doubles as the indicator of whether the page is shared at all, which
// is what it has always been (it used to be an inert button with a tooltip).
//
// Where the messages go. 0.6.27.7's rule is a summary in the flash and the detail
// under the field. This keeps that shape but moves the summary into an Alert
// inside the dialog, because the dialog is modal: anything flashed while it is
// open sits behind the backdrop, where it can't be read. That holds for every
// message raised with the dialog open — a failed share, a failed unshare, a
// network drop, a stale token, and the confirmation after someone is removed.
//
// The one message that does go to the flash is a successful share, because the
// dialog closes then and the flash is the only thing left to read it in.

import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import {
  People as PeopleIcon,
  Lock as LockIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material'

import { LightTooltip, tooltipContent } from '../../../components/LightToolTip'
import { fieldErrorText, splitFieldErrors } from '../../../components/devise/deviseErrors'
import { failureMessage, refreshCsrfToken } from '../../../components/devise/deviseRequest'
import useSubmitting from '../../../hooks/useSubmitting'
import { clearFlashAlert, clearFlashNotice, setFlashNotice } from '../ui/uiSlice'
import {
  createShareAsync,
  destroyShareAsync,
  selectShareRecordsForPage,
} from './sharesSlice'

// The field the server reports a share's failures on.
const GUEST_FIELD = 'guest_login_or_email'

// A share's states, as the server spells them.
const ACCEPTED = 'accepted'
const PENDING = 'pending'
const DECLINED = 'declined'

const classes = {
  button: {
    color: 'white',
    backgroundColor: 'inherit',
    '& .MuiSvgIcon-root': {
      marginRight: { xs: 0, sm: '0.3em' },
    },
    '&:hover': {
      backgroundColor: (theme) => theme.palette.info.light,
    },
  },
  buttonLabel: {
    display: { xs: 'none', sm: 'inline' },
  },
}

// What to call a guest. Every account has a login, so the fallback is only for an
// unmigrated legacy record.
const guestName = (share) => share?.guest_login || 'an account with no login'

// Where a share stands, said under the person's name. Since 0.6.27.10 a share is
// an offer, so "shared with" no longer means "can read": these three are the whole
// difference, and the owner has no other way to know which they are looking at.
//
// The declined line says the offer can be made again, because that is the question
// a refusal raises. Sharing the page with them replaces the declined share, so
// there is nothing for the owner to clear out first.
const stateCaption = (share) => {
  switch (share.state) {
    case ACCEPTED:
      return 'Can read this page'
    case DECLINED:
      return 'Declined — you can offer it again'
    default:
      return `Waiting for them to answer — they can't read it yet`
  }
}

// "stop sharing" fits a share someone is reading. For an offer nobody has answered,
// or one they refused, there is nothing to stop: the row is all that is left.
const removeLabel = (share) =>
  share.state === ACCEPTED
    ? `stop sharing with ${guestName(share)}`
    : `remove the share with ${guestName(share)}`

const peopleCount = (count) => `${count} ${count === 1 ? 'person' : 'people'}`

export default function ShareButton({ pageId }) {
  const dispatch = useDispatch()
  const sharesSelector = useMemo(() => selectShareRecordsForPage(pageId), [pageId])
  const shares = useSelector(sharesSelector)

  const [isOpen, setIsOpen] = useState(false)
  const [guest, setGuest] = useState('')
  const [fieldErrors, setFieldErrors] = useState({})
  // The dialog's own message: {severity, text}, or null for none.
  const [message, setMessage] = useState(null)
  // true while sharing with a new guest, or a share's id while removing
  // that one -- either way, only one action across the whole dialog at a
  // time (the existing behaviour this replaces), but the spinner can
  // still land on the specific button that was actually clicked.
  const [isWorking, submit] = useSubmitting()

  // The count is of people who can actually read the page, which since 0.6.27.10
  // means the ones who accepted. Counting an offer nobody has answered as a share
  // would tell the owner the page is out when it isn't.
  const readers = shares.filter((share) => share.state === ACCEPTED)
  const waiting = shares.filter((share) => share.state === PENDING)
  const shareCount = readers.length

  // Three states for the button, because the page itself has three: read by
  // someone, offered to someone and waiting, or nobody's but mine. The middle one
  // keeps the lock, since nobody can read it yet.
  const [shareIcon, shareText, buttonLabel] =
    shareCount > 0
      ? [<PeopleIcon />, `Shared with ${peopleCount(shareCount)}`, 'Shared']
      : waiting.length > 0
        ? [
            <LockIcon />,
            `Private: waiting for ${peopleCount(waiting.length)} to answer`,
            'Offered',
          ]
        : [<LockIcon />, 'Private to only me', 'Share']

  // What the dialog says before the list. The list itself marks each person, so
  // this only has to set the scene. It builds its own wording rather than reusing
  // the button's: "Private: waiting for ..." is right on a button, where it has to
  // stand alone, and says "private" twice in a sentence that has already said it.
  const summaryText =
    shareCount > 0
      ? `${shareText}. They can read this page, but not change it.`
      : waiting.length > 0
        ? `This page is private until someone accepts it. Waiting for ` +
          `${peopleCount(waiting.length)} to answer.`
        : 'This page is private. Share it with someone to let them read it.'

  ///////////////////////////////////////////////
  //
  // Events
  //

  const handleOpen = () => {
    setGuest('')
    setFieldErrors({})
    setMessage(null)
    // Drop whatever the flash is still holding from an earlier task. A flash has
    // no timeout, so a message such as "Signed in successfully." sits there until
    // something replaces it, and it would be left behind the dialog saying
    // unrelated things while the dialog talks about sharing. Opening this is a
    // deliberate move to something new, so the page goes quiet first.
    dispatch(clearFlashNotice())
    dispatch(clearFlashAlert())
    setIsOpen(true)
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  // A request that didn't get the answer the dialog wanted. `failureMessage`
  // gives the wording for a network drop, a stale CSRF token, or an unexpected
  // body, and a stale token also gets a new one so the next try works — that is
  // what `showFailedRequest` would have done, minus the flash nobody could read.
  const showFailure = (result) => {
    setMessage({ severity: 'error', text: failureMessage(result) })
    if (result.failure === 'stale_token') {
      refreshCsrfToken(dispatch)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setFieldErrors({})
    setMessage(null)

    submit(async () => {
      const result = await dispatch(
        createShareAsync({ pageId, guestLoginOrEmail: guest })
      )

      // The dialog stays open on any failure, so the typing isn't lost.
      if (!result || result.failure) {
        showFailure(result)
        return
      }

      if (result.response.status === 201) {
        dispatch(setFlashNotice(`Shared this page with ${guestName(result.json)}.`))
        setIsOpen(false)
        return
      }

      if (result.response.status === 422) {
        const { fieldErrors: errors, flashMessage } = splitFieldErrors(
          result.json,
          [GUEST_FIELD],
          'the page from being shared'
        )
        setFieldErrors(errors)
        setMessage({ severity: 'error', text: flashMessage })
        return
      }

      showFailure(result)
    })
  }

  const handleRemove = (share) => () => {
    setMessage(null)

    submit(async () => {
      const result = await dispatch(destroyShareAsync(share.id))

      if (!result || result.failure || result.response.status !== 204) {
        showFailure(result)
        return
      }

      // Clear the form as well. Whatever is in it was typed to share the page with
      // someone, and after a removal it is most likely the name of the person who
      // has just gone — which reads as if they are about to be added back. Found in
      // your hand check of 2026-10-03.
      setGuest('')
      setFieldErrors({})

      // "Stopped sharing" is wrong for someone who never had the page: an offer they
      // hadn't answered, or one they refused. Only a reader was being shared with.
      setMessage({
        severity: 'success',
        text:
          share.state === ACCEPTED
            ? `Stopped sharing this page with ${guestName(share)}.`
            : `Removed the share with ${guestName(share)}.`,
      })
    }, share.id)
  }

  ///////////////////////////////////////////////
  //
  // Markup
  //

  return (
    <React.Fragment>
      <LightTooltip title={tooltipContent(shareText, shareIcon)}>
        {/* "Shared" once it is, so the state of the page reads off the button
            itself rather than only from the icon and the tooltip. */}
        <Button sx={classes.button} onClick={handleOpen} aria-label='share this page'>
          {shareIcon}
          {/* Just the icon in a narrow window, so the menu bar stays on one
              line; the tooltip still says the whole thing. */}
          <Box component='span' sx={classes.buttonLabel}>{buttonLabel}</Box>
        </Button>
      </LightTooltip>

      <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth='sm'>
        <DialogTitle>Share this page</DialogTitle>
        <DialogContent>
          <Typography variant='body2' gutterBottom>
            {summaryText}
          </Typography>

          {Boolean(message) && (
            <Alert severity={message.severity} sx={{ mt: 2, mb: 1 }}>
              {message.text}
            </Alert>
          )}

          {/* Everyone the page has been offered to, whatever they have done about
              it, and not just the ones who accepted: an offer waiting and an offer
              refused are both things the owner has to be able to see. */}
          {shares.length > 0 && (
            <List dense sx={{ mt: 1 }}>
              {shares.map((share) => (
                <ListItem
                  key={`share-${share.id}`}
                  secondaryAction={
                    <IconButton
                      edge='end'
                      aria-label={removeLabel(share)}
                      onClick={handleRemove(share)}
                      disabled={Boolean(isWorking)}
                      loading={isWorking === share.id}
                    >
                      <PersonRemoveIcon />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={guestName(share)}
                    secondary={stateCaption(share)}
                  />
                </ListItem>
              ))}
            </List>
          )}
          {/* Who it is shared with comes first, and the way to add someone sits
              under that list, where it reads as the next line of it. Once there
              is a list, the form needs saying what it is for: on its own under a
              list of people it could as easily be a search box. When the page is
              private the paragraph at the top has already said it. */}
          {shares.length > 0 && (
            <Typography variant='subtitle2' sx={{ mt: 2 }}>
              Share with someone else
            </Typography>
          )}
          <Stack
            component='form'
            onSubmit={handleSubmit}
            direction='row'
            spacing={1}
            sx={{ mt: 2, alignItems: 'flex-start' }}
          >
            <TextField
              label='Login or email address'
              value={guest}
              onChange={(e) => setGuest(e.target.value)}
              error={Boolean(fieldErrorText(fieldErrors, GUEST_FIELD))}
              helperText={
                fieldErrorText(fieldErrors, GUEST_FIELD) ||
                'They need an account already. Ask them which to use.'
              }
              fullWidth
              autoFocus
            />
            <Button
              type='submit'
              variant='contained'
              disabled={Boolean(isWorking)}
              loading={isWorking === true}
              sx={{ mt: 1 }}
            >
              Share
            </Button>
          </Stack>

       </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Done</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  )
}
